<script>
import _ from 'lodash'
import Card from '../components/slds/Card'
import DataTable from '../components/slds/DataTable'
import Picklist from '../components/slds/Picklist'
import Spinner from '../components/Spinner'

const validationMap = {
  'not null': {
    validate: (key, val, index, otherValues) => val !== null && val !== undefined && val.length > 0,
    message: (vm) => vm.$t('validation.required')
  },
  'unique': {
    validate: (key, val, index, otherValues) => otherValues.map(e => e[key]).filter(e => e === val).length === 0,
    message: (vm) => vm.$t('validation.duplicateValue')
  }
}

export default {
  components: {
    Card,
    DataTable,
    Picklist,
    Spinner
  },
  computed: {
    availableTables () {
      return Object.keys(this.tables).map(table => {
        return {
          label: table,
          value: table
        }
      })
    },
    tableHeaders () {
      if (this.table && this.table.columns) {
        return {
          names: this.table.columns.map(col => col.name),
          options: this.table.columns.map(col => col.type.replace('boolean', 'checkbox').replace('enum', 'picklist')),
          picklistValues: this.table.columns.map(col => col.options)
        }
      } else {
        return {}
      }
    }
  },
  data () {
    return {
      deletedRows: [],
      loading: false,
      selectedTable: '',
      tables: {},
      table: {}
    }
  },
  async created () {
    const tables = await this.getTables()
    this.tables = tables
  },
  methods: {
    addRow () {
      this.$refs.dataTable.goToLastPage()
      const newRow = this.table.columns
        .reduce((acc, val) => {
          switch (val.type) {
            case 'text':
              acc[val.name] = ''
              break
            case 'text[]':
              acc[val.name] = []
              break
            case 'boolean':
              acc[val.name] = false
              break
            default:
              acc[val.name] = ''
          }
          return acc
        }, {})

      this.table.rows.push(newRow)
    },
    async getTables () {
      try {
        const tables = await this.$store.dispatch('domainTablesConfiguration/getTables')
        return tables
      } catch (error) {
        this.showToast('error', this.$t('general.error'), 'text', error)
      }
    },
    handleDeleteRow (event) {
      const { rowIndex } = event

      const hasId = !!this.table.rows[rowIndex].id
      if (hasId) {
        this.deletedRows.push(this.table.rows[rowIndex].id)
      }
      this.table.rows.splice(rowIndex, 1)
    },
    async save () {
      try {
        const [ schema, tableName ] = this.table.name.split('.')
        const selectedTable = this.tables[tableName]
        // Calculate delta from original values
        const insertValues = []
        const updateValues = []

        const isValid = this.table.rows.every((row, index) => this.validate(row, index, selectedTable))

        if (isValid) {
          this.table.rows.forEach((row, index) => {
          // If this is a new row
            if (!row.id) {
              const isEmpty = Object.values(row).every(val => !val.length)
              if (!isEmpty) {
                insertValues.push(row)
              }
            } else {
            // If row has been edited
              const originalRow = selectedTable.rows.find(r => r.id === row.id)
              if (JSON.stringify(originalRow) !== JSON.stringify(row)) {
                updateValues.push(row)
              }
            }
          })

          const obj = {
            [tableName]: {
              name: `${schema}.${tableName}`,
              delete: this.deletedRows,
              insert: {
                rows: insertValues
              },
              update: {
                rows: updateValues
              }
            }
          }

          this.loading = true

          try {
            await this.$store.dispatch('domainTablesConfiguration/updateTable', { tableName, body: obj })
            this.tables = await this.getTables()
            this.showToast('success', this.$t('general.success'), 'text', this.$t('message.update'))
            this.loading = false
          } catch (error) {
            this.showToast('error', this.$t('general.error'), 'text', error)
            this.loading = false
          }
        }
      } catch (error) {
        console.error(error)
        this.showToast('error', this.$t('validation.invalidData'), 'text', this.$t('validation.invalidDataMessage'))
      }
    },
    validate (row, index, selectedTable) {
      return Object.entries(row).every(([key, value]) => {
        const column = selectedTable.columns.find(column => column.name === key)
        if (column) {
          const { validation } = column
          if (validation) {
            const valid = validation.every(valRule => {
              const otherElements = selectedTable.rows.filter((e, i) => i < index)
              const isValid = !!(validationMap[valRule].validate(key, value, index, otherElements))
              if (!isValid) {
                this.$refs.dataTable.$refs[`input_${index}__${key}`][0].error = validationMap[valRule].message(this)
              }
              return isValid
            })
            return valid
          } else {
            return true
          }
        } else {
          return true
        }
      })
    }
  },
  watch: {
    selectedTable (val) {
      this.deletedRows = []
      this.table = _.cloneDeep(this.tables[val])
    }
  }
}
</script>
<template>
  <div class="">
    <article class="slds-card">
      <div class="slds-card__header slds-grid">
        <header class="slds-media slds-media_center slds-has-flexi-truncate">
          <div class="slds-media__figure">
            <span class="slds-icon_container slds-icon-standard-user">
              <svg class="slds-icon slds-icon-action-user" aria-hidden="true">
                <use xlink:href="../assets/symbols.svg#table" />
              </svg>
            </span>
          </div>
          <div class="slds-media__body">
            <h2 class="slds-card__header-title">
              <span class="slds-card__header-link slds-truncate">{{ $t('domainTablesConfiguration.title') }}</span>
            </h2>
          </div>
        </header>
      </div>
    </article>

    <Spinner v-if="loading || !tables || !Object.keys(tables).length" />

    <template v-else>
      <!-- Picklist -->
      <Card additional-classes="slds-card_boundary" class="slds-m-top--small">
        <Picklist :options="availableTables" v-model="selectedTable" />
      </Card>
      <!-- Table -->
      <transition name="fade">
        <Card
          additional-classes="slds-card_boundary slds-height"
          v-if="Object.keys(table).length"
          :title="$t('domainTablesConfiguration.rows')">
          <div class="slds-button-group" role="group" slot="actions">
            <button class="slds-button slds-button_neutral" :disabled="loading" @click="save">{{$t('action.save')}}</button>
            <button class="slds-button slds-button_neutral" :disabled="loading" @click="addRow">{{$t('action.new')}}</button>
          </div>

          <DataTable
            :headers="tableHeaders"
            :rows="table.rows"
            @deleteRow="handleDeleteRow"
            has-delete-action
            mode="edit"
            ref="dataTable" />
        </Card>
      </transition>
    </template>
  </div>
</template>
<style lang="scss" scoped>
.full-height {
  height: 100%;
}
</style>
